package org.example.zone360.pages.coachDetails.coachAssistAi

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import org.example.zone360.models.ClubPayment
import org.example.zone360.models.GetSessionsData
import org.example.zone360.models.GetSingleCoachData
import org.example.zone360.models.QuickAction
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

object AssistAIConstants {
    val quickCoachReportQuestions: Map<String, List<QuickAction>> = mapOf(

        "📊 Reporting" to listOf(
            QuickAction(
                title = "Coach Talk Ratio",
                query = """
        Analyze my Coach Talk Ratio over the last 12 months. Compare me against club targets and coaches in my age group. Identify situations where I talk too much or too little. Explain how my communication style affects player learning, engagement, and decision-making. Recommend practical strategies to improve my communication efficiency.

        Include:
        • My talk %
        • Club benchmark
        • Trend analysis
        • Over-coaching moments
        • Under-coaching moments
        • Recommended communication habits
    """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Session Intensity",
                query = """
        Analyze my Session Intensity trends over the last 12 months. Explain whether my sessions are appropriately challenging for my age group and compare my intensity levels to club benchmarks. Identify periods of low and high intensity and recommend ways to optimize session design.

        Include:
        • Average intensity
        • Weekly trend
        • Peak sessions
        • Low engagement periods
        • Improvement recommendations
    """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Game Realism",
                query = """
        Evaluate the realism of my training sessions. Analyze how often my practices include opposition, transitions, decision-making opportunities, directional play, and game pressure. Identify gaps and recommend adjustments that better replicate match demands.

        Include:
        • Overall score
        • Dimension breakdown
        • Missing game elements
        • Session redesign recommendations
    """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Development Orientation",
                query = """
        Analyze my Development Orientation score. Evaluate whether my coaching behaviors prioritize player learning, risk-taking, and long-term growth. Identify situations where I may unintentionally prioritize results over development and recommend adjustments.

        Include:
        • Current orientation score
        • Trend analysis
        • Growth opportunities
        • Coaching recommendations
    """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Tactical Complexity",
                query = """
        Analyze the tactical complexity of my sessions. Compare my tactical demands with age-group recommendations and explain whether my coaching appropriately challenges players. Identify concepts that may be too advanced or underdeveloped.

        Include:
        • Complexity score
        • Concept frequency
        • Development risks
        • Suggested progressions
    """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Topic Diversity",
                query = """
        Evaluate the diversity of my training topics. Identify areas that are overemphasized and underrepresented. Explain how topic distribution affects player development and recommend ways to create a more balanced curriculum.

        Include:
        • Diversity score
        • Top topics
        • Missing topics
        • Recommended adjustments
    """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Methodology Consistency",
                query = """
        Analyze my Methodology Consistency score. Compare my coaching behaviors to club principles and identify where I consistently reinforce or neglect club methodology. Recommend practical strategies to improve alignment.

        Include:
        • Alignment score
        • Principle adherence
        • Missing behaviors
        • Action plan
    """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Cognitive Training",
                query = """
        Evaluate the cognitive demands of my sessions. Analyze how often players are required to scan, solve problems, make decisions, and perceive information under pressure. Recommend ways to improve cognitive stimulation.

        Include:
        • Overall score
        • Dimension breakdown
        • Missed opportunities
        • Recommendations
    """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Competitive Environment",
                query = """
        Analyze the competitiveness of my training sessions. Determine whether I create enough accountability, challenge, and meaningful consequences. Recommend strategies to improve motivation and competitive learning.

        Include:
        • Environment score
        • Component analysis
        • Missing elements
        • Recommendations
    """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Match Analysis",
                query = """
        Analyze my team's last five matches. Evaluate our performance in possession, out of possession, and transitions. Identify our biggest strengths and weaknesses, explain root causes, and generate a prioritized training plan with recommended training allocation and expected performance improvements.

        Include:
        • Match performance score
        • Trend analysis
        • Strengths
        • Weaknesses
        • Root causes
        • Training priorities
        • Suggested weekly training plan
    """.trimIndent(),
                forceReport = true
            )
        ),

        "📋 Club Terminology" to listOf(
            QuickAction(
                title = "Make a table breakdown of my use of official club terminology throughout my sessions.",
                query = "Make a table breakdown of my use of official club terminology throughout my sessions."
            ),
            QuickAction(
                title = "Score my communication style and provide the percentage of communication categories I used across all sessions.",
                query = "Score my communication style and provide the percentage of communication categories I used across all sessions."
            ),
            QuickAction(
                title = "Which club philosophy terms did I use most and least frequently?",
                query = "Which club philosophy terms did I use most and least frequently?"
            ),
            QuickAction(
                title = "Did I correctly apply club terminology in tactical situations?",
                query = "Did I correctly apply club terminology in tactical situations?"
            )
        ),

        "🎯 Training Session Design" to listOf(
            QuickAction(
                title = "Did my instructions reflect the club game model and philosophy? Break it down for me.",
                query = "Did my instructions reflect the club game model and philosophy? Break it down for me."
            ),
            QuickAction(
                title = "Were my coaching interventions clear, concise, and actionable in my sessions?",
                query = "Were my coaching interventions clear, concise, and actionable in my sessions?"
            ),
            QuickAction(
                title = "Did I create game-realistic scenarios through my verbal coaching cues?",
                query = "Did I create game-realistic scenarios through my verbal coaching cues?"
            )
        ),

        "🏃 In Possession" to listOf(
            QuickAction(
                title = "How often did I reinforce attacking shape, spacing, and line-breaking actions in my sessions?",
                query = "How often did I reinforce attacking shape, spacing, and line-breaking actions in my sessions?"
            ),
            QuickAction(
                title = "Did I verbally encourage switching play or exploiting strong side vs weak side opportunities?",
                query = "Did I verbally encourage switching play or exploiting strong side vs weak side opportunities?"
            ),
            QuickAction(
                title = "Did I use language that promoted proactive possession and progression under pressure?",
                query = "Did I use language that promoted proactive possession and progression under pressure?"
            )
        ),

        "🛡️ Out of Possession" to listOf(
            QuickAction(
                title = "How frequently did I reinforce compactness and defensive shape?",
                query = "How frequently did I reinforce compactness and defensive shape?"
            ),
            QuickAction(
                title = "Did I clearly communicate pressing triggers, pressure, and recovery behaviors?",
                query = "Did I clearly communicate pressing triggers, pressure, and recovery behaviors?"
            ),
            QuickAction(
                title = "Did I emphasize protecting central areas and the penalty box?",
                query = "Did I emphasize protecting central areas and the penalty box?"
            )
        ),

        "🌱 Player Development" to listOf(
            QuickAction(
                title = "Did I encourage player decision-making rather than only giving commands?",
                query = "Did I encourage player decision-making rather than only giving commands?"
            ),
            QuickAction(
                title = "Did I reinforce positioning, body shape, movement, and awareness?",
                query = "Did I reinforce positioning, body shape, movement, and awareness?"
            ),
            QuickAction(
                title = "Did I provide corrective feedback that supported player development?",
                query = "Did I provide corrective feedback that supported player development?"
            )
        ),

        "⚖️ Tactical Consistency" to listOf(
            QuickAction(
                title = "Did my verbal instructions remain consistent with the club philosophy throughout my sessions?",
                query = "Did my verbal instructions remain consistent with the club philosophy throughout my sessions?"
            ),
            QuickAction(
                title = "Were my tactical instructions consistent between attacking and defensive phases?",
                query = "Were my tactical instructions consistent between attacking and defensive phases?"
            ),
            QuickAction(
                title = "Did I adapt situations while still maintaining club principles?",
                query = "Did I adapt situations while still maintaining club principles?"
            )
        ),

        "🗣️ Communication & Leadership" to listOf(
            QuickAction(
                title = "Was my communication organized, confident, and easy to understand?",
                query = "Was my communication organized, confident, and easy to understand?"
            ),
            QuickAction(
                title = "Did I maintain a positive and constructive communication style?",
                query = "Did I maintain a positive and constructive communication style?"
            ),
            QuickAction(
                title = "Did I balance instruction, correction, and encouragement effectively?",
                query = "Did I balance instruction, correction, and encouragement effectively?"
            )
        )
    )
    val quickDashboardActionQuestions: Map<String, List<QuickAction>> = mapOf(

        "📊 Reporting" to listOf(

            QuickAction(
                title = "Coach Talk Ratio",
                query = """
            Compare all coaches' Coach Talk Ratios over the last 12 months. Rank coaches from highest to lowest talk percentage. Compare each coach against club and age-group targets. Identify who is over-coaching or under-coaching and explain how their communication style impacts player engagement and learning opportunities. Highlight trends and recommend specific interventions for each coach.

            Include:
            • Coach ranking
            • Club average
            • Age-group average
            • Weekly trend
            • Alignment status
            • Risk assessment
            • Recommended actions
        """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Session Intensity",
                query = """
            Compare Session Intensity scores across all coaches for the last 12 months. Identify coaches consistently delivering high, moderate, and low-intensity sessions. Explain the impact of intensity levels on player development and identify coaches whose sessions may be too demanding or insufficiently challenging.

            Include:
            • Coach rankings
            • Intensity trends
            • Age-group benchmarks
            • Consistency scores
            • Development risk indicators
        """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Game Realism",
                query = """
            Compare Game Realism scores for all coaches. Evaluate how effectively coaches replicate match conditions through opposition, decision-making opportunities, transitions, directional play, and pressure. Identify coaches creating the most representative learning environments.

            Include:
            • Coach rankings
            • Radar comparisons
            • Weakest dimensions
            • Best practices
            • Club average
        """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Development Orientation",
                query = """
            Compare all coaches' Development Orientation scores. Identify coaches prioritizing long-term player development versus short-term results. Explain how coaching behaviors align with developmental principles and identify coaches whose behaviors may undermine player growth.

            Include:
            • Rankings
            • Club philosophy alignment
            • Development risk indicators
            • Coaching behavior examples
        """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Tactical Complexity",
                query = """
            Compare Tactical Complexity across all coaches. Evaluate whether tactical content is age appropriate and identify coaches who under-challenge or overload players. Determine which concepts are most frequently coached and assess alignment with player developmental stages.

            Include:
            • Complexity rankings
            • Age-group benchmarks
            • Most coached concepts
            • Overload indicators
        """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Topic Diversity",
                query = """
            Compare Topic Diversity scores across all coaches. Identify which coaches deliver the broadest and narrowest training experiences. Analyze topic distribution and determine whether players are receiving balanced development across technical, tactical, physical, and cognitive areas.

            Include:
            • Rankings
            • Coverage maps
            • Topic gaps
            • Redundancy indicators
        """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Methodology Consistency",
                query = """
            Compare all coaches' Methodology Consistency scores. Evaluate how consistently coaches reinforce club principles, language, and playing identity. Identify coaches who best represent the club methodology and coaches who require support.

            Include:
            • Club DNA rankings
            • Principle usage
            • Consistency trends
            • Alignment risks
        """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Cognitive Training",
                query = """
            Compare Cognitive Training scores across all coaches. Evaluate how effectively coaches develop decision-making, scanning, perception, problem-solving, and tactical awareness. Identify which coaches provide the richest cognitive learning environments.

            Include:
            • Rankings
            • Cognitive dimension scores
            • Best practices
            • Development gaps
        """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Competitive Environment",
                query = """
            Compare Competitive Environment scores across all coaches. Evaluate how coaches use scoring systems, accountability, constraints, time pressure, and winning conditions to create competitive learning experiences. Identify coaches who create optimal challenge environments.

            Include:
            • Rankings
            • Competitive components
            • Balance indicators
            • Development risks
        """.trimIndent(),
                forceReport = true
            ),

            QuickAction(
                title = "Match Analysis",
                query = """
            Compare match performance reports across all coaches and teams over the last month. Identify trends in in-possession play, out-of-possession organization, and transitions. Determine common strengths and weaknesses across the club and prioritize development areas that will have the greatest impact on future performance.

            Include:
            • Team rankings
            • Club-wide strengths
            • Club-wide weaknesses
            • Root cause analysis
            • Recommended club priorities
            • Predicted impact
        """.trimIndent(),
                forceReport = true
            )
        ),
        "📋 Club Terminology" to listOf(
            QuickAction(
                title = "Make a table breakdown of each coach use of official club terminology throughout the sessions.",
                query = "Make a table breakdown of each coach use of official club terminology throughout the sessions."
            ),
            QuickAction(
                title = "Score each coach based on this communication style and provide the percentage of communication categories used across all sessions.",
                query = "Score each coach based on this communication style and provide the percentage of communication categories used across all sessions."
            ),
            QuickAction(
                title = "Which club philosophy terms were used most and least frequently?",
                query = "Which club philosophy terms were used most and least frequently?"
            ),
            QuickAction(
                title = "Did the coach correctly apply club terminology in tactical situations?",
                query = "Did the coach correctly apply club terminology in tactical situations?"
            )
        ),

        "🎯 Training Session Design" to listOf(
            QuickAction(
                title = "Did the coaches instructions reflect the club game model and philosophy? make a table for comparison",
                query = "Did the coaches instructions reflect the club game model and philosophy? make a table for comparison"
            ),
            QuickAction(
                title = "Were coaching interventions clear, concise, and actionable in the session?",
                query = "Were coaching interventions clear, concise, and actionable in the session?"
            ),
            QuickAction(
                title = "Did the coach create game-realistic scenarios through verbal coaching cues?",
                query = "Did the coach create game-realistic scenarios through verbal coaching cues?"
            )
        ),

        "🏃 In Possession" to listOf(
            QuickAction(
                title = "How often did the coach reinforce attacking shape, spacing, and line-breaking actions in the session?",
                query = "How often did the coach reinforce attacking shape, spacing, and line-breaking actions in the session?"
            ),
            QuickAction(
                title = "Did the coach verbally encourage switching play or exploiting strong side vs weak side opportunities?",
                query = "Did the coach verbally encourage switching play or exploiting strong side vs weak side opportunities?"
            ),
            QuickAction(
                title = "Did the coach use language that promoted proactive possession and progression under pressure?",
                query = "Did the coach use language that promoted proactive possession and progression under pressure?"
            )
        ),

        "🛡️ Out of Possession" to listOf(
            QuickAction(
                title = "How frequently did the coach reinforce compactness and defensive shape?",
                query = "How frequently did the coach reinforce compactness and defensive shape?"
            ),
            QuickAction(
                title = "Did the coach clearly communicate pressing triggers, pressure, and recovery behaviors?",
                query = "Did the coach clearly communicate pressing triggers, pressure, and recovery behaviors?"
            ),
            QuickAction(
                title = "Did the coach emphasize protecting central areas and the penalty box?",
                query = "Did the coach emphasize protecting central areas and the penalty box?"
            )
        ),

        "🌱 Player Development" to listOf(
            QuickAction(
                title = "Did the coach encourage player decision-making rather than only giving commands?",
                query = "Did the coach encourage player decision-making rather than only giving commands?"
            ),
            QuickAction(
                title = "Did the coach reinforce positioning, body shape, movement, and awareness?",
                query = "Did the coach reinforce positioning, body shape, movement, and awareness?"
            ),
            QuickAction(
                title = "Did the coach provide corrective feedback that supported player development?",
                query = "Did the coach provide corrective feedback that supported player development?"
            )
        ),

        "⚖️ Tactical Consistency" to listOf(
            QuickAction(
                title = "Did the coach's verbal instructions remain consistent with the club philosophy throughout the session?",
                query = "Did the coach's verbal instructions remain consistent with the club philosophy throughout the session?"
            ),
            QuickAction(
                title = "Were the coach's tactical instructions consistent between attacking and defensive phases?",
                query = "Were the coach's tactical instructions consistent between attacking and defensive phases?"
            ),
            QuickAction(
                title = "Did the coach adapt situations while still maintaining club principles?",
                query = "Did the coach adapt situations while still maintaining club principles?"
            )
        ),

        "🗣️ Communication & Leadership" to listOf(
            QuickAction(
                title = "Was the coach's communication organized, confident, and easy to understand?",
                query = "Was the coach's communication organized, confident, and easy to understand?"
            ),
            QuickAction(
                title = "Did the coach maintain a positive and constructive communication style?",
                query = "Did the coach maintain a positive and constructive communication style?"
            ),
            QuickAction(
                title = "Did the coach balance instruction, correction, and encouragement effectively?",
                query = "Did the coach balance instruction, correction, and encouragement effectively?"
            )
        )
    )
}

fun generateContextFromSession(
    sessionList: List<GetSessionsData>,
    coachInfo: GetSingleCoachData
): String {
    val latestSessionSummary = sessionList
        .maxByOrNull { it.sessionDateTime }
        ?.let { session ->
            """
            Recent Session Summary:
            - Type: ${session.sessionType}
            - Date: ${session.sessionDateTime}
            - Summary: ${session.summary.takeIf { it.isNotBlank() } ?: "N/A"}
            """.trimIndent()
        }

    val baseContext = buildString {
        append("${coachInfo.firstName} ${coachInfo.lastName} is the Head Coach of ${coachInfo.teams?.joinToString() ?: "N/A"}, ")
        append("currently based in ${coachInfo.nationality}. ")
        append("He coaches at the ${coachInfo.level} level and is managing the ${coachInfo.teams?.joinToString() ?: "N/A"} team(s). ")
        append("His current role is listed as ${coachInfo.job}. ")
        append("So far, ${coachInfo.firstName} has conducted ${coachInfo.count} sessions, ")
        append("recording a total of ${coachInfo.recordTime} minutes with ${coachInfo.totalKeywords} keywords identified. ")
        append("His training emphasis currently reflects ${coachInfo.donut}.")
    }

    return if (latestSessionSummary.isNullOrBlank()) {
        baseContext
    } else {
        "$baseContext Here is recent session recorded by ${coachInfo.firstName}: $latestSessionSummary"
    }
}


fun cleanMarkdown(text: String): String {
    return text
        .replace(Regex("""(\*{1,3}|#{1,6})"""), "") // removes *, **, ***, #, ##
        .replace(Regex("""\n{2,}"""), "\n") // normalizes newlines
        .trim()
}

fun isPaymentActive(expiredOn: String): Boolean {
    return try {
        val expirationInstant = Instant.parse(expiredOn)
        Clock.System.now() < expirationInstant
    } catch (e: Exception) {
        false
    }
}

fun isClubPaymentActive(payment: ClubPayment): Boolean {
    return try {
        val expirationInstant = Instant.parse(payment.expiredOn)
        val now = Clock.System.now()
        now < expirationInstant
    } catch (e: Exception) {
        false // If parsing fails, treat as expired
    }
}
